import React from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import { useTier } from "@/contexts/AuthContext";

/**
 * Renders children only if the current tier meets `min`. Otherwise shows a
 * restrained upgrade prompt — single CTA, mono copy, no marketing fluff.
 */
export const TierGate = ({ min = "starter", featureLabel, children }) => {
    const { tier, meets } = useTier();
    if (meets(min)) return children;

    return (
        <div
            data-testid={`tier-gate-${min}`}
            className="border border-[#d8dcdd] bg-[#ffffff] p-7 lg:p-9 flex flex-col gap-4"
        >
            <div className="inline-flex items-center gap-2 tc-mono text-[10px] text-[#5a6669] tracking-widest uppercase">
                <Lock size={12} strokeWidth={1.5} />
                Requires {min} tier
            </div>
            <h3 className="text-[18px] font-medium tracking-tight text-[#0e1416]">
                {featureLabel || "This module"} is available on {min === "professional" ? "Professional" : "Starter"}.
            </h3>
            <p className="text-[13px] text-[#5a6669] leading-[1.6] max-w-[60ch]">
                You&apos;re currently on the{" "}
                <span className="tc-mono text-[#0e1416]">{tier}</span> tier.
                Upgrade to unlock this workflow without re-implementation or
                data migration.
            </p>
            <div>
                <Link
                    to="/pricing"
                    data-testid={`tier-gate-${min}-cta`}
                    className="group inline-flex items-center gap-2 border border-[#d8dcdd] hover:border-[#01696f] hover:text-[#01696f] text-[#0e1416] px-4 py-2.5 text-[13px] transition-colors"
                >
                    <span>View plans</span>
                    <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
            </div>
        </div>
    );
};

/**
 * DemoLockOverlay — blurs interactive children behind a hairline card for
 * canvas-heavy surfaces. Scaffolded now; consumed by Phase 2B canvas modules.
 */
export const DemoLockOverlay = ({ children, message }) => {
    const { tier } = useTier();
    if (tier !== "demo") return children;

    return (
        <div className="relative" data-testid="demo-lock-overlay">
            <div className="pointer-events-none select-none filter blur-[2px] opacity-70">
                {children}
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-[#f6f7f6]/40 backdrop-blur-[1px]">
                <div className="border border-[#d8dcdd] bg-[#ffffff] px-6 py-5 max-w-[360px] text-center flex flex-col gap-3">
                    <div className="tc-mono text-[10px] text-[#5a6669] tracking-widest uppercase">
                        Demo tier — preview only
                    </div>
                    <p className="text-[13px] text-[#0e1416] leading-[1.55]">
                        {message ||
                            "Interactive canvas is locked on the Demo tier."}
                    </p>
                    <Link
                        to="/pricing"
                        className="tc-mono text-[12px] text-[#01696f] hover:text-[#015a60] transition-colors"
                    >
                        Upgrade →
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default TierGate;
